import { FC, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import CellBox from "../../../components/common/table/CellBox";
import BorderRow from "../../../components/common/table/BorderRow";
import { BasicTableRowStyles } from "../../../components/common/Common.styles";
import StatusDisplay from "../../../components/common/table/StatusDisplay";
import TooltipTypography from "../../../components/common/TooltipTypography";
import { Download } from "@mui/icons-material";
import { InvitationCodeStyles } from "../Employees.styles";
import { useNavigate } from "react-router-dom";
import { GroupItem } from "../../../types/groupItem";
import ListAltIcon from '@mui/icons-material/ListAlt';
import Dialog from "@mui/material/Dialog";
import EmployeeModulesDialog from "./EmployeeModulesDialog";
import { EmployeeUserType } from "../../../types/employeeType";
import { useLocale } from "../../../hooks/UseLocale";
import EditIcon from "@mui/icons-material/Edit";
import CreateOrEditEmployeeDialog from "./CreateOrEditEmployeeDialog";
import { usePermissionChecker } from "../../../hooks/usePermissionChecker";
import { PermissionEntityType } from "../../../types/permissionType";
import DeleteBtn from "../../../components/common/table/DeleteBtn";
import { useAppDispatch } from "../../../hooks/StoreHooks";
import { deleteEmployeeThunk } from "../../../store/thunk/employeesThunk";
import { mapThunkTypeToResponseValue } from "../../../common/logic/mapThunkTypeToResponseValue";
import { setProcessingRequest, setResponseMessage, setResponseValue } from "../../../store/reducers/tableManagementReducer";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

interface EmployeesRowProps {
  userData: EmployeeUserType;
  groupList: GroupItem[];
}

const EmployeesRow: FC<EmployeesRowProps> = ({
  userData,
  groupList,
}) => {
  const { t } = useTranslation();
  const [openModulesDialog, setOpenModulesDialog] = useState(false);
  const navigate = useNavigate();
  const locale = useLocale();
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const dispatch = useAppDispatch();

  const { userHasPermission } = usePermissionChecker();
  const canDeleteEmployees = userHasPermission(
    PermissionEntityType.Employees,
    "Delete"
  );

  const handleRowClick = () => {
    if (openEditDialog || openModulesDialog) return;
    navigate(`/${locale}/employees/${userData.id}`);
  };

  const handleDeleteEmployee = async () => {
    dispatch(setProcessingRequest(true));
    const res = await dispatch(deleteEmployeeThunk(userData.id));
    dispatch(setResponseValue(mapThunkTypeToResponseValue(res.type)));
    dispatch(setResponseMessage(t("dialogModals.itemDeletedUser")));
  };

  const handleOpenModulesDialog = () => {
    setOpenModulesDialog(true);
  };
  const handleCloseModulesDialog = () => {
    setOpenModulesDialog(false);
  };

  return (
    <BasicTableRowStyles onClick={handleRowClick} style={{ cursor: "pointer" }}>
      <CellBox firstCol stickyFirstCol>
        <TooltipTypography
          value={userData.firstName}
        />
      </CellBox>
      <CellBox>
        <TooltipTypography
          value={userData.lastName}
        />
      </CellBox>
      <CellBox>
        <TooltipTypography
          value={userData.birthDate
            ? dayjs(userData.birthDate).format("DD.MM.YYYY")
            : ""}
        />
      </CellBox>
      <CellBox>
        <TooltipTypography
          value={userData.employmentDate
            ? dayjs(userData.employmentDate).format("DD.MM.YYYY")
            : ""}
        />
      </CellBox>
      <CellBox>
        <TooltipTypography
          value={userData.position}
        />
      </CellBox>
      <CellBox contentPosition="center">
        <TooltipTypography
          value={userData.employeeNumber}
        />
      </CellBox>
      <CellBox>
        <StatusDisplay status={userData.status} />
      </CellBox>
      <CellBox>
        {userData.groupIds?.length ? (
          <Tooltip
            title={groupList
              .filter(g => userData.groupIds.includes(g.id))
              .map(g => g.name)
              .join(", ")}
            arrow
          >
            <Box>
              <Typography variant="body2" noWrap>
                {userData.groupIds.length === 1
                  ? groupList.find(g => g.id === userData.groupIds[0])?.name || "Unknown"
                  : `[${userData.groupIds.length} Groups...]`}
              </Typography>
            </Box>
          </Tooltip>
        ) : (
          <Typography variant="caption">-</Typography>
        )}
      </CellBox>
      <CellBox>
        <TooltipTypography
          value={userData.subCompanyName || "-"}
        />
      </CellBox>
      <CellBox contentPosition="center">
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleOpenModulesDialog();
          }}
        >
          <ListAltIcon />
        </IconButton>
      </CellBox>
      <CellBox>
        <InvitationCodeStyles>
          <Typography variant="caption" sx={{ fontSize: 16 }}>{userData.registrationCode}</Typography>
          {userData.onboardingFileDownloadUrl && (
            <a
              href={userData.onboardingFileDownloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <IconButton>
                <Download />
              </IconButton>
            </a>
          )}
        </InvitationCodeStyles>
      </CellBox>
      <CellBox contentPosition="center">
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setOpenEditDialog(true);
          }}
        >
          <EditIcon />
        </IconButton>
        {canDeleteEmployees && (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <DeleteBtn
              isCreateNewMode={false}
              dialogMessage={t("dialogModals.confirmDeleteUser")}
              handleOnDelete={handleDeleteEmployee}
            />
          </Box>
        )}
      </CellBox>
      <BorderRow />

      <Dialog
        open={openModulesDialog}
        onClose={handleCloseModulesDialog}
        maxWidth="md"
        fullWidth
      >
        <Box onClick={(e) => e.stopPropagation()}>
          <EmployeeModulesDialog
            userId={userData.id}
            userName={userData.firstName + " " + userData.lastName}
            open={openModulesDialog}
            onClose={() => setOpenModulesDialog(false)}
          />
        </Box>
      </Dialog>

      <CreateOrEditEmployeeDialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        groupList={groupList}
        mode="edit"
        employee={userData}
      />

    </BasicTableRowStyles>
  );
};

export default EmployeesRow;
